import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Building2, MapPin, Users, ArrowRight, RefreshCw, ExternalLink, Trash2 } from "lucide-react";
import { ROLE_LABELS } from "@/auth";
import { toast } from "sonner";

export default function SuperAdminHome() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        api.get("/superadmin/subaccounts"),
        api.get("/dashboard/stats"),
      ]);
      setSubs(s.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get("/superadmin/subaccounts"),
          api.get("/dashboard/stats"),
        ]);
        if (cancelled) return;
        setSubs(s.data);
        setStats(st.data);
      } catch (e) {
        if (!cancelled) toast.error("Error cargando datos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reloadCatalog = async (id) => {
    try {
      await api.post(`/superadmin/subaccounts/${id}/reload-catalog`);
      toast.success("Catálogo recargándose en segundo plano");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error("Error recargando catálogo");
    }
  };

  const openDashboard = async (id) => {
    try {
      const r = await api.post(`/superadmin/subaccounts/${id}/impersonate`);
      // Open dashboard in new tab with one-shot token
      const url = `/dashboard?_t=${encodeURIComponent(r.data.token)}`;
      const w = window.open(url, "_blank");
      if (!w) toast.error("Permite ventanas emergentes para abrir el dashboard");
    } catch (e) {
      toast.error("No se pudo acceder al dashboard");
    }
  };

  const deleteSubaccount = async (s) => {
    const confirmText = `Esta acción eliminará permanentemente la subcuenta "${s.name}" y TODOS sus datos: usuarios, voluntarios, enlaces de reclutamiento, casillas y catálogo geo cacheado.\n\nNo se puede deshacer.\n\nEscribe el slug "${s.slug}" para confirmar:`;
    const typed = window.prompt(confirmText);
    if (typed === null) return;
    if (typed.trim() !== s.slug) {
      toast.error("Slug incorrecto, operación cancelada");
      return;
    }
    try {
      const r = await api.delete(`/superadmin/subaccounts/${s.id}`);
      const d = r.data?.deleted || {};
      toast.success(
        `Subcuenta "${s.name}" eliminada. ` +
        `Usuarios: ${d.users || 0} · Voluntarios: ${d.volunteers || 0} · Enlaces: ${d.recruit_links || 0}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la subcuenta");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-admin-home">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="tracking-label text-blue-700">Panel Super Administrador</span>
            <h1 className="mt-2 font-display font-black text-slate-900 text-3xl sm:text-4xl">
              Subcuentas
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Administra todas las campañas y sus accesos independientes.
            </p>
          </div>
          <Button onClick={() => nav("/super-admin/subaccounts/new")} className="btn-primary h-11" data-testid="create-subaccount-button">
            <Plus className="w-4 h-4 mr-2" /> Nueva subcuenta
          </Button>
        </div>

        {/* NOCTUA access */}
        <div className="mt-8 surface-card p-5 border-l-4 border-l-blue-600 flex flex-wrap items-center justify-between gap-3" data-testid="noctua-access-card">
          <div>
            <div className="tracking-label text-blue-700">Módulo adicional</div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">NOCTUA · Inteligencia Electoral</h2>
            <p className="text-sm text-slate-600 mt-1">Mapa electoral, lectura territorial, app de campo, panel de inteligencia y motor de escenarios.</p>
          </div>
          <Button onClick={() => nav("/noctua/mapa")} className="bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="open-noctua-button">
            Abrir NOCTUA <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
          <StatCard label="Subcuentas" value={stats?.subaccounts ?? "—"} testid="stat-subs" />
          <StatCard label="Usuarios" value={stats?.users ?? "—"} testid="stat-users" />
          <StatCard label="Voluntarios" value={stats?.volunteers ?? "—"} testid="stat-vol" />
          <StatCard label="Enlaces" value={stats?.links ?? "—"} testid="stat-links" />
          <StatCard label="Pendientes" value={stats?.pending_links ?? "—"} accent testid="stat-pending" />
        </div>

        {/* Subaccounts list */}
        <div className="mt-10 surface-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-display font-bold text-lg text-slate-900">Subcuentas registradas</h2>
            <span className="text-xs text-slate-500" data-testid="subs-count">{subs.length} totales</span>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando…</div>
          ) : subs.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-10 h-10 mx-auto text-slate-400" />
              <p className="mt-4 text-slate-600">No hay subcuentas creadas todavía.</p>
              <Button onClick={() => nav("/super-admin/subaccounts/new")} className="btn-primary mt-4" data-testid="empty-create-button">
                Crear la primera
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {subs.map((s) => (
                <div key={s.id} className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid={`subaccount-row-${s.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-slate-900 text-base">{s.name}</h3>
                      <Badge variant="outline" className="uppercase tracking-wider text-[10px]">
                        {s.scope}
                      </Badge>
                      {s.catalog_loaded ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200">Catálogo listo</Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200">Cargando catálogo…</Badge>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-slate-600 flex items-center gap-4 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.entidad_nombre}{s.municipio_nombre ? ` · ${s.municipio_nombre}` : ""}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {s.candidate_name}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.enabled_roles.map((r) => (
                        <span key={r} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded uppercase tracking-wider">
                          {ROLE_LABELS[r] || r}
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Slug: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{s.slug}</code>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => openDashboard(s.id)} data-testid={`open-dashboard-${s.slug}`}>
                      <ExternalLink className="w-4 h-4 mr-1.5" /> Dashboard
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => reloadCatalog(s.id)} data-testid={`reload-catalog-${s.slug}`}>
                      <RefreshCw className="w-4 h-4 mr-1.5" /> Catálogo
                    </Button>
                    <Link to={`/super-admin/subaccounts/${s.id}`} data-testid={`view-subaccount-${s.slug}`}>
                      <Button className="btn-primary" size="sm">
                        Gestionar <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteSubaccount(s)}
                      className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      data-testid={`delete-subaccount-${s.slug}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className={`surface-card p-5 ${accent ? "border-blue-200 bg-blue-50" : ""}`} data-testid={testid}>
      <div className="tracking-label text-slate-500">{label}</div>
      <div className="mt-2 font-display font-black text-3xl text-slate-900">{value}</div>
    </div>
  );
}
